import { Project, ProjectStatus, UserRole } from '@avanzu/types';
import {
  getCarryForwardMap,
  resolveSchemaVersionForTaxYear,
} from '@avanzu/rdti-schema';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useProject } from '../../../contexts/ProjectContext';
import formatCurrency from '../../../utils/formatCurrency';
import { getProjectQuotaInfo } from '../../../utils/quota';

interface ProjectsByYear {
  [taxYear: string]: Project[];
}

interface ProjectSelectorProps {
  projects: Project[];
  selectedProject: Project | null;
  onProjectSelect: (project: Project) => void;
  onProjectEdit: (project: Project) => void;
  onCreateProject: (taxYear?: string, carryForwardFromProjectId?: string) => void;
  loading: boolean;
}

export const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  projects,
  selectedProject,
  onProjectSelect,
  onProjectEdit,
  onCreateProject,
  loading,
}) => {
  const { currentProjectId } = useProject();
  const { currentOrganisation } = useAuth();
  const navigate = useNavigate();
  const isViewer = currentOrganisation?.role === UserRole.VIEWER;
  const canUpgrade =
    currentOrganisation?.role === UserRole.ADMIN || currentOrganisation?.role === UserRole.OWNER;
  const [showYearDialog, setShowYearDialog] = React.useState(false);
  const [selectedYear, setSelectedYear] = React.useState<string>('');
  const [carryForwardFrom, setCarryForwardFrom] = React.useState<string>('');

  const getTaxYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const options = [];
    for (let i = -1; i <= 3; i++) {
      const year = currentYear + i;
      options.push(`${year}-${(year + 1).toString().slice(-2)}`);
    }
    return options;
  };

  const handleOpenCreateDialog = () => {
    const currentYear = new Date().getFullYear();
    setSelectedYear(`${currentYear}-${(currentYear + 1).toString().slice(-2)}`);
    setShowYearDialog(true);
  };

  const handleCreateProject = () => {
    setShowYearDialog(false);
    onCreateProject(selectedYear, carryForwardFrom || undefined);
    setCarryForwardFrom('');
  };

  // Projects eligible to carry-forward from: existing projects whose schema
  // version has a defined mapping to the target year's version. Today that
  // is FY24 → FY25.
  const targetVersion = selectedYear ? resolveSchemaVersionForTaxYear(selectedYear) : null;
  const carryForwardCandidates = React.useMemo(() => {
    if (!targetVersion) return [] as Project[];
    return projects.filter((p) => {
      const sourceVersion = p.schemaVersion ?? resolveSchemaVersionForTaxYear(p.taxYear);
      if (sourceVersion === targetVersion) return false;
      return Boolean(getCarryForwardMap(sourceVersion, targetVersion));
    });
  }, [projects, targetVersion]);

  const groupProjectsByYear = (): ProjectsByYear => {
    return projects.reduce((acc, project) => {
      const year = project.taxYear;
      if (!acc[year]) {
        acc[year] = [];
      }
      acc[year].push(project);
      return acc;
    }, {} as ProjectsByYear);
  };

  const sortTaxYears = (years: string[]): string[] => {
    return years.sort((a, b) => {
      // Handle formats like "2024-25" by comparing the first year
      const yearA = parseInt(a.split('-')[0]);
      const yearB = parseInt(b.split('-')[0]);
      return yearB - yearA; // Most recent first
    });
  };

  const formatTaxYear = (taxYear: string): string => {
    return `FY ${taxYear}`;
  };

  const getStatusColor = (status: ProjectStatus) => {
    switch (status) {
      case ProjectStatus.ACTIVE:
        return 'success';
      case ProjectStatus.COMPLETED:
        return 'info';
      case ProjectStatus.SUBMITTED:
        return 'warning';
      case ProjectStatus.DRAFT:
      default:
        return 'default';
    }
  };

  const handleSelectProject = async (project: Project) => {
    try {
      onProjectSelect(project);
    } catch (error) {
      console.error('Failed to select project:', error);
      // You might want to show a toast notification here
    }
  };

  const handleEditProject = (project: Project, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent ListItemButton click
    onProjectEdit(project);
  };

  const renderProjectList = () => {
    if (loading) {
      return (
        <Box p={2} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            Loading projects...
          </Typography>
        </Box>
      );
    }

    if (projects.length === 0) {
      return (
        <Box p={3} textAlign="center">
          <Typography variant="body2" color="text.secondary" gutterBottom>
            No R&D projects found
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Create your first project to get started
          </Typography>
        </Box>
      );
    }

    const projectsByYear = groupProjectsByYear();
    const sortedYears = sortTaxYears(Object.keys(projectsByYear));

    return (
      <Box>
        {sortedYears.map((taxYear) => {
          const yearProjects = projectsByYear[taxYear];

          return (
            <Box key={taxYear} sx={{ mb: 3 }}>
              {/* Year Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: 1,
                  px: 1.5,
                  py: 1,
                  backgroundColor: 'grey.50',
                  borderRadius: 1,
                }}
              >
                <Chip
                  label={formatTaxYear(taxYear)}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Typography variant="caption" color="text.secondary">
                  {yearProjects.length} project{yearProjects.length !== 1 ? 's' : ''}
                </Typography>
              </Box>

              {/* Projects for this year */}
              <List disablePadding>
                {yearProjects.map((project) => {
                  const isCurrentProject = currentProjectId === project.id;
                  const isEditingProject = selectedProject?.id === project.id;

                  return (
                    <ListItem key={project.id} disablePadding sx={{ mb: 1 }}>
                      <ListItemButton
                        selected={isEditingProject}
                        onClick={() => handleSelectProject(project)}
                        sx={{
                          borderRadius: 1,
                          //  border: isEditingProject ? 2 : 1,
                          border: 1,
                          borderColor: isEditingProject ? 'primary.main' : 'divider',
                          backgroundColor: isEditingProject ? 'primary.50' : '#ffffff',
                          '&.Mui-selected': {
                            backgroundColor: 'action.selected',
                          },
                          '&:hover': {
                            backgroundColor: isEditingProject ? 'primary.100' : 'grey.50',
                          },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="subtitle2" noWrap>
                                {project.name}
                              </Typography>
                              <Chip
                                label={project.status}
                                size="small"
                                color={getStatusColor(project.status) as any}
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                              {isCurrentProject && (
                                <Chip
                                  label="Selected"
                                  size="small"
                                  color="primary"
                                  variant="filled"
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              Budget: {formatCurrency(project.totalBudget)} | Tax Year:{' '}
                              {project.taxYear}
                            </Typography>
                          }
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}>
                          <Tooltip title="Edit project">
                            <IconButton
                              size="small"
                              color="default"
                              onClick={(e) => handleEditProject(project, e)}
                              sx={{
                                backgroundColor: 'grey.50',
                                '&:hover': {
                                  backgroundColor: 'grey.100',
                                },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
              {/* Show quota info if limit is reached or close to limit */}
              {currentOrganisation &&
                !isViewer &&
                (() => {
                  const quotaInfo = getProjectQuotaInfo(
                    currentOrganisation,
                    taxYear,
                    yearProjects.length
                  );

                  // Only show if at limit or near limit
                  if (!quotaInfo.isAtLimit && !quotaInfo.isNearLimit) return null;

                  return (
                    <Box sx={{ px: 1.5, mt: 2 }}>
                      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                        {quotaInfo.isAtLimit
                          ? `You've reached your limit of ${quotaInfo.limit} project${quotaInfo.limit !== 1 ? 's' : ''} for this financial year.`
                          : `${quotaInfo.remaining} project${quotaInfo.remaining !== 1 ? 's' : ''} remaining for this financial year.`}
                      </Typography>
                      {quotaInfo.isAtLimit && canUpgrade && (
                        <Button
                          variant="outlined"
                          onClick={() => {
                            navigate('/settings/plan');
                          }}
                          startIcon={<AddIcon />}
                          fullWidth
                        >
                          Upgrade to create more
                        </Button>
                      )}
                    </Box>
                  );
                })()}
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with Create Button */}
      {!isViewer && (
        <Box sx={{ mb: 2, pl: 1.5, pr: 1.5 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
            fullWidth
          >
            Create Project
          </Button>
        </Box>
      )}

      {/* Projects List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>{renderProjectList()}</Box>

      {/* Year Selection Dialog */}
      <Dialog open={showYearDialog} onClose={() => setShowYearDialog(false)}>
        <DialogTitle>Create New Project</DialogTitle>
        <DialogContent>
          <Box sx={{ minWidth: 300, pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select the financial year for the new project:
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Financial Year</InputLabel>
              <Select
                value={selectedYear}
                label="Financial Year"
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {getTaxYearOptions().map((year) => {
                  const projectsInYear = projects.filter((p) => p.taxYear === year).length;
                  const quotaInfo = getProjectQuotaInfo(currentOrganisation, year, projectsInYear);

                  return (
                    <MenuItem key={year} value={year} disabled={quotaInfo.isAtLimit}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <span>FY {year}</span>
                        {!quotaInfo.isUnlimited && (
                          <Chip
                            label={
                              quotaInfo.isAtLimit
                                ? 'Limit reached'
                                : `${quotaInfo.current}/${quotaInfo.limit}`
                            }
                            size="small"
                            color={quotaInfo.isAtLimit ? 'error' : 'default'}
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {carryForwardCandidates.length > 0 && (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Pre-fill from existing project (optional)</InputLabel>
                <Select
                  value={carryForwardFrom}
                  label="Pre-fill from existing project (optional)"
                  onChange={(e) => setCarryForwardFrom(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Start blank</em>
                  </MenuItem>
                  {carryForwardCandidates.map((p) => {
                    const sourceVersion =
                      p.schemaVersion ?? resolveSchemaVersionForTaxYear(p.taxYear);
                    return (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name} ({sourceVersion}, FY {p.taxYear})
                      </MenuItem>
                    );
                  })}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Compatible fields will be copied as a starting point. {targetVersion}-only
                  questions (behavioural-additionality, beneficial ownership, plant &amp; facilities,
                  dominant-purpose explanation, etc.) stay blank — they have no FY24 equivalent.
                </Typography>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowYearDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateProject} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectSelector;
